import Vue from 'vue';
import Component from 'vue-class-component';
import { required } from 'vuelidate/lib/validators';
import { Watch } from 'vue-property-decorator';
import moment from 'moment';
import * as i18next from 'i18next';
import router from '../../../router';
import { G2S } from 'core/g2s';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

export interface DestinationViewModel {
    name?: string;
    parentName?: string;
    iataCode?: string;
}

export interface FlightSearchFormModel {
    params: flights.FlightSearchParams;
    destinationFrom?: DestinationViewModel;
    destinationTo?: DestinationViewModel;
    destinationLookupFrom: string;
    destinationLookupTo: string;
    submitted: boolean;
}

export function toSameAsFrom(value, parentVm) {
    if (parentVm.from === value) {
        return false;
    }
    return true;
}

export function departureMinDate(value) {
    const now = moment().startOf('day');
    if (moment(value).isBefore(now)) {
        return false;
    }
    return true;
}

export function returnBeforeDate(value, parentVm) {
    if (parentVm.departure
        && moment(value).isBefore(moment(parentVm.departure))
        && parentVm.roundtrip) {
        return false;
    }
    return true;
}

export function passengersTotal(value) {
    if (value.adults + value.children + value.infants > 9) {
        return false;
    }
    return true;
}

export function infantsWithAdults(value, parentVm) {
    if (value > 0 && value > parentVm.adults) {
        return false;
    }
    return true;
}

@WithRender
@Component({
    validations: {
        model: {
            params: {
                from: { required },
                to: { required, toSameAsFrom },
                departure: { departureMinDate },
                return: { returnBeforeDate },
                passengers: {
                    passengersTotal,
                    infants: { infantsWithAdults }
                }
            }
        }
    }
})
export class FlightSearchFormComponent extends Vue {
    // There are no flights after 331 day
    maxDate = moment().add(331, 'day').startOf('day');

    model: FlightSearchFormModel = {
        params: {
            from: '',
            to: '',
            departure: moment().add(7, 'days').startOf('day').toDate(),
            return: moment().add(14, 'days').startOf('day').toDate(),
            roundtrip: true,
            class: flights.BookingClass.Economic,
            passengers: {
                adults: 1,
                children: 0,
                infants: 0
            }
        },
        destinationFrom: undefined,
        destinationTo: undefined,
        destinationLookupFrom: '',
        destinationLookupTo: '',
        submitted: false
    }

    isInputStarted: boolean = false;
    isDataWasSelected: boolean = false;

    searchType: string = 'roundtrip';

    modalDestinationMode: string = '';
    modalDateMode: string = '';

    @Watch('model.destinationLookupFrom', {deep: true})
    onChangeDestinationLookupFrom() {
        if(!this.model.destinationLookupFrom){
            this.model.params.from = '';
            (<any>this).model.destinationFrom = {
                name: '',
                parentName: '',
                iataCode: ''
            };
        }
    }

    @Watch('model.destinationLookupTo', {deep: true})
    onChangeDestinationLookupTo() {
        if(!this.model.destinationLookupTo){
            this.model.params.to = '';
            (<any>this).model.destinationTo = {
                name: '',
                parentName: '',
                iataCode: ''
            };
        }
    }
    

    @Watch('model.destinationFrom.iataCode', {deep: true})
    onChangeDestinationFrom() {
        if(this.model.destinationFrom?.name){
            this.model.destinationLookupFrom = this.model.destinationFrom.name;
        }
    }
    @Watch('model.destinationTo.iataCode', {deep: true})
    onChangeDestinationTo() {
        if(this.model.destinationTo?.name){
            this.model.destinationLookupTo = this.model.destinationTo.name;
        }
    }

    get classLabel() {
        return flights.BookingClass.classLabel(this.model.params.class);
    }
    
    get classOptions() {
        const result: { label: string; value: string }[] = [];

        flights.BookingClass.listForSearch().forEach((c) => {
            result.push({
                label: flights.BookingClass.classLabel(c),
                value: c
            });
        });

        return result;
    }

    get calendarMonths() {
        const result: {
            date: moment.Moment;
            days: any[];
        }[] = [];

        const now = moment().startOf('day');
        const date = now.clone().startOf('month');

        while (date.diff(this.maxDate) < 0) {
            const item: {
                date: moment.Moment;
                days: any[];
            } = {
                date: date.clone(),
                days: []
            };

            const startWeek = date.clone().isoWeekday(G2S.config.locale !== 'ru' ? 'Sunday' : 'Monday').startOf('month').week();
            const monthStart = date.clone().isoWeekday(G2S.config.locale !== 'ru' ? 'Sunday' : 'Monday').week(startWeek).startOf('week');
            const monthEnd = date.clone().endOf('month');

            while (monthStart.diff(monthEnd) < 0) {
                let d: any = {};

                if (monthStart.month() == date.month()) {
                    const isYesterday = monthStart.diff(now, 'days') == -1;
                    d = {
                        number: monthStart.date(),
                        date: (monthStart.isSameOrAfter(now) && monthStart.isSameOrBefore(this.maxDate)) ? monthStart.format('YYYY-MM-DD') : '',
                        isYesterday,
                        isToday: monthStart.diff(now) === 0,
                        isDeparture: monthStart.isSame(this.model.params.departure!),
                        isReturn: this.model.params.roundtrip && monthStart.isSame(this.model.params.return!),
                        isCurrent: false,
                        isActive: false
                    };
                }
                if (d.isDeparture
                    || d.isReturn
                    || (this.model.params.roundtrip
                        && monthStart.isAfter(this.model.params.departure!)
                        && monthStart.isBefore(this.model.params.return!)
                    )
                ) {
                    d.isActive = true;
                }

                if (d.isDeparture || d.isReturn) {
                    d.isCurrent = true;
                    d.isActive = false;
                }

                item.days.push(d);

                monthStart.add(1, 'days');
            }

            result.push(item);

            date.add(1, 'months');
        }
        return result;
    }

    get caledarWeekdays() {
        return moment.weekdaysShort(true);
    }

    get totalPassengers() {
        return this.model.params.passengers.adults
            + this.model.params.passengers.children
            + this.model.params.passengers.infants;
    }

    dateToUppercase(text: string) {
        if (!text) return text;
        return text.split(' ').map(s => s[0].toUpperCase() + s.slice(1)).join(' ');
    }

    startedSearchInput() {
        if (!this.isInputStarted) {
            (<any>this).$ga.sendEvent('flights.search', 'submit.error', 'flights.search.submit.input.started');
            this.isInputStarted = true;
        }
    }

    getDestinationViewModel(apiDestination: api.Destination): DestinationViewModel {
        const result: DestinationViewModel = {
            name: apiDestination.localizedName,
            parentName: '',
            iataCode: apiDestination.iataCode
        };

        if (apiDestination.city
            && apiDestination.city.localizedName != apiDestination.localizedName) {
            result.parentName = apiDestination.city.localizedName;
        }

        if (apiDestination.country) {
            if (result.parentName && result.parentName.length > 0) {
                result.parentName += ', ';
            }
            result.parentName += apiDestination.country.localizedName;
        }

        return result;
    }
    mounted() {
        const historyItems: flights.SearchHistoryItem[] = new flights.FlightSearchService().getHistoryItems();

        if (historyItems && historyItems.length) {
            const url = historyItems[0].url.split('/');
            const passengersFromUrl = url[url.length - 2];

            this.model.params.from = url[1];
            this.model.params.to = url[2];
            this.model.params.departure = historyItems[0].departure;
            this.model.params.return = historyItems[0].return;
            this.model.params.roundtrip = historyItems[0].return ? true : false;
            this.model.params.class = historyItems[0].class;
            this.model.params.passengers = {
                adults: parseInt(passengersFromUrl[0]),
                children: parseInt(passengersFromUrl[1]),
                infants: parseInt(passengersFromUrl[2])
            };
            this.model.destinationFrom = {
                name: '',
                parentName: '',
                iataCode: url[1]
            };
            this.model.destinationTo = {
                name: '',
                parentName: '',
                iataCode: url[2]
            };
            this.model.destinationLookupFrom = historyItems[0].from;
            this.model.destinationLookupTo = historyItems[0].to;
        }

        if (this.$store.getters.flightSearchParams) {
            // There are stored search params - let's use them
            this.model.params.from = this.$store.getters.flightSearchParams.from;
            this.model.params.to = this.$store.getters.flightSearchParams.to;
            this.model.params.departure = this.$store.getters.flightSearchParams.departure;
            this.model.params.return = this.$store.getters.flightSearchParams.return;
            this.model.params.roundtrip = this.$store.getters.flightSearchParams.roundtrip;
            this.model.params.class = this.$store.getters.flightSearchParams.class;
            this.model.params.passengers.adults = this.$store.getters.flightSearchParams.passengers.adults;
            this.model.params.passengers.children = this.$store.getters.flightSearchParams.passengers.children;
            this.model.params.passengers.infants = this.$store.getters.flightSearchParams.passengers.infants;
            this.model.destinationFrom = {
                name: '',
                parentName: '',
                iataCode: this.model.params.from
            };
            this.model.destinationTo = {
                name: '',
                parentName: '',
                iataCode: this.model.params.to
            };
        }

        if (historyItems && historyItems.length
            || this.$store.getters.flightSearchParams) {
            // Get destination details
            new api.DestinationService().list([this.model.params.from, this.model.params.to]).then(
                (result: api.Destination[]) => {
                    let d = utils.find(result, (item) => {
                        return item.iataCode === this.model.params.from;
                    });

                    if (d) {
                        const m = this.getDestinationViewModel(d);
                        if (this.model.destinationFrom) {
                            this.model.destinationFrom.name = m.name;
                            this.model.destinationFrom.parentName = m.parentName;
                        }
                    }

                    d = utils.find(result, (item) => {
                        return item.iataCode === this.model.params.to;
                    });

                    if (d) {
                        const m = this.getDestinationViewModel(d);
                        if (this.model.destinationTo) {
                            this.model.destinationTo.name = m.name;
                            this.model.destinationTo.parentName = m.parentName;
                        }
                    }
                }
            );
        }
    }

    onDestinationSwap() {
        if (this.model.destinationFrom) {
            const tmp = {
                name: this.model.destinationFrom.name,
                parentName: this.model.destinationFrom.parentName,
                iataCode: this.model.destinationFrom.iataCode
            };

            this.model.destinationFrom = this.model.destinationTo;
            this.model.destinationTo = tmp;

            if (this.model.destinationFrom) {
                this.model.params.from = this.model.destinationFrom!.iataCode!;
            } else {
                this.model.params.from = '';
            }
            this.model.params.to = this.model.destinationTo!.iataCode!;
        } else if (this.model.destinationTo) {
            const tmp = {
                name: this.model.destinationTo.name,
                parentName: this.model.destinationTo.parentName,
                iataCode: this.model.destinationTo.iataCode
            };

            this.model.destinationTo = this.model.destinationFrom;
            this.model.destinationFrom = tmp;

            if (this.model.destinationTo) {
                this.model.params.to = this.model.destinationTo!.iataCode!;
            } else {
                this.model.params.to = '';
            }
            this.model.params.from = this.model.destinationFrom!.iataCode!;
        }
    }

    onSubmit() {
        setTimeout(() => {
            (<any>this).$ga.sendPageview('/flights/search/submit', 'Flights / Search / Submit');

            this.$v.model!.params!.$touch();

            if (this.$v.model!.$error) {
                let error = undefined;
                (<any>this).$ga.sendEvent('flights.search', 'submit.error', 'flights.search.submit.error');
                (<any>this).$ga.reachGoal('flights.search.submit.error');

                if (!error
                    && !this.$v.model!.params!.from!.required) {
                    error = i18next.t('error_flight_destination_from_empty');
                }

                if (!error
                    && !this.$v.model!.params!.to!.required) {
                    error = i18next.t('error_flight_destination_to_empty');
                }

                if (!error
                    && !this.$v.model!.params!.to!.toSameAsFrom) {
                    error = i18next.t('error_flight_destination_to_same_as_from');
                }

                if (!error
                    && !this.$v.model!.params!.departure!.departureMinDate) {
                    error = i18next.t('error_flight_departure_in_the_past');
                }

                if (!error
                    && !this.$v.model!.params!.return!.returnBeforeDate) {
                    error = i18next.t('error_flight_return_before_departure');
                }

                if (!error
                    && this.$v.model!.params!.passengers!.$error) {
                    error = i18next.t('error_flight_passengers');
                }

                if (!error) {
                    error = i18next.t('error_fields');
                }

                (<any>this.$root.$refs).messageDialog.show({
                    message: error
                });
            } else {
                this.model.submitted = true;

                const flightNavigationService = new flights.FlightNavigationService();

                flightNavigationService.onBeforeNavigation(this.model.params);

                router.push(flightNavigationService.getSearchResultsUrl(this.model.params));
            }
        }, 300);
    }

    onModalDestinationStart(mode: string) {
        const modeUpper = mode[0].toUpperCase() + mode.slice(1);
        this.model['destinationLookup' + modeUpper] = '';
        this.model.params[mode] = '';
        this.model['destination' + modeUpper] = {
            name: '',
            parentName: '',
            iataCode: ''
        };

        this.modalDestinationMode = mode;
    }

    onModalDestinationSelected(item: any) {
        if (this.modalDestinationMode === 'from') {
            this.model.params.from = item.value;
            this.model.destinationFrom = {
                name: item.label,
                parentName: item.sublabel,
                iataCode: item.stamp
            };
        } else {
            this.model.params.to = item.value;
            this.model.destinationTo = {
                name: item.label,
                parentName: item.sublabel,
                iataCode: item.stamp
            };
        }

    }

    onDestinationBlur(destination: string) {
        const autocompliteRef = 'autocompliteDestination' + destination,
            inputRef = 'inputDestination' + destination,
            modalDestination = 'destination' + destination;

        if ((<any>this.$refs)[autocompliteRef].results.length) {
            this.onModalDestinationSelected((<any>this.$refs)[autocompliteRef].results[0]);
        } else if (!this.model[modalDestination].iataCode && (<any>this.$refs)[inputRef].model) {
            new api.DestinationService().lookup((<any>this.$refs)[inputRef].model).then(
                (result: api.Destination[]) => {
                    const destinations = this.destinationsConvert(result);
                    if (destinations.length) {
                        this.onModalDestinationSelected(destinations[0]);
                    }
                }
            );
            
        }
    }

    onModalDateOpen(mode: string) {
        if (mode == 'return' && !this.model.params.roundtrip) {
            return;
        }

        this.modalDateMode = mode;

        (<any>this.$refs).modalDate.show();

        this.startedSearchInput();
    }

    dataWasSelected(){
        if (!this.isDataWasSelected) {
            this.isDataWasSelected = true;
        }
    }
    
    onModalDateOpened() {
        let d: string = '';

        if (this.modalDateMode === 'departure') {
            if (this.model.params.departure) {
                d = moment(this.model.params.departure).format('YYYYMM');
            }
        } else {
            if (this.model.params.return) {
                d = moment(this.model.params.return).format('YYYYMM');
            }
        }

        if (d) {
            utils.ScrollTo.element('#modalDate .q-modal-layout-content', '#month' + d, false);
        }
    }

    onModalDateSelect($event) {
        const d = $event.target.getAttribute('data-date');
        
        if (d) {
            const selectedDate = moment(d);
           
            if (this.modalDateMode === 'departure') {
                if(this.model.params.return && this.model.params.roundtrip){
                    if (moment(this.model.params.return).isBefore(selectedDate)) {
                        this.model.params.departure = selectedDate.toDate();
                        (<any>this).model.params.roundtrip = !(<any>this).model.params.roundtrip;
                        (<any>this).model.params.return = !(<any>this).model.params.return;
                        this.modalDateMode = 'return';
                    } else {
                        if(!this.isDataWasSelected){
                            this.model.params.departure = selectedDate.toDate();
                            this.modalDateMode = 'return';
                        } else {
                            (<any>this.$refs).modalDate.hide();
                            this.model.params.departure = selectedDate.toDate();
                        }
                    }
                } else {
                    this.modalDateMode = 'return';
                    this.model.params.departure = selectedDate.toDate();
                }
            }
            else if (this.modalDateMode === 'return') {
                if (moment(this.model.params.departure!).isAfter(selectedDate) && this.model.params.return) {
                    this.model.params.departure = selectedDate.toDate();
                } else if(moment(this.model.params.departure!).isAfter(selectedDate)) {
                    this.model.params.return = this.model.params.departure;
                    (<any>this).model.params.departure = selectedDate.toDate();
                    this.model.params.roundtrip = true;
                } else {
                    this.model.params.return = selectedDate.toDate();
                    this.model.params.roundtrip = true;
                    (<any>this.$refs).modalDate.hide();
                }
            } else {
                this.model.params[this.modalDestinationMode] = selectedDate.toDate();
                (<any>this.$refs).modalDate.hide();
            }
            this.dataWasSelected();
        }
    }

    onModalDateOneWay() {
        (<any>this.$refs).modalDate.hide();

        if ((<any>this).model.params.return) {
            (<any>this).model.params.return = !(<any>this).model.params.return;
        }
        this.model.params.roundtrip = false;
    }

    onModalPassengersChange(target: string, v: number) {
        this.startedSearchInput();
        switch (target) {
            case 'adults':
                if (v > 0) {
                    if (this.model.params.passengers.adults < 9) {
                        this.model.params.passengers.adults++;
                    }
                } else {
                    if (this.model.params.passengers.adults > 1) {
                        this.model.params.passengers.adults--;
                    }
                }
                break;
            case 'children':
                if (v > 0) {
                    if (this.model.params.passengers.children < 8) {
                        this.model.params.passengers.children++;
                    }
                } else {
                    if (this.model.params.passengers.children > 0) {
                        this.model.params.passengers.children--;
                    }
                }
                break;
            case 'infants':
                if (v > 0) {
                    if (this.model.params.passengers.infants < 4) {
                        this.model.params.passengers.infants++;
                    }
                } else {
                    if (this.model.params.passengers.infants > 0) {
                        this.model.params.passengers.infants--;
                    }
                }
                break;
        }
    }

    onModalPassengersSubmit() {
        this.$v.model!.params!.passengers!.$touch();

        if (this.$v.model!.params!.passengers!.$error) {
            let error = undefined;

            // Child validation should be checked first
            if (!error
                && this.$v.model!.params!.passengers!.infants!.$error) {
                error = i18next.t('error_flight_passengers_infants_noadult');
            }
            if (!error
                && this.$v.model!.params!.passengers!.$error) {
                error = i18next.t('error_flight_passengers_limit');
            }

            if (!error) {
                error = i18next.t('error_fields');
            }

            (<any>this.$root.$refs).messageDialog.show({
                title: i18next.t('error'),
                message: error
            });
        } else {
            (<any>this.$refs).modalPassengers.hide();
        }
    }

    showDestinations(ref: string) {
        if ((<any>this.$refs)['autocompliteDestination' + ref].results.length === 1) {
            (<any>this.$refs)['autocompliteDestination' + ref].setValue((<any>this.$refs)['autocompliteDestination' + ref].results[0]);
            
            if (ref === 'From') {
                (<any>this.$refs).inputDestinationTo.focus();
                this.onModalDestinationStart('to');
            } else {
                (<any>this.$refs).inputDestinationTo.blur();
            }
        }
    }

    destinationsConvert(result) {
        const destinations: {
            value?: string;
            label?: string;
            sublabel?: string;
            stamp?: string;
        }[] = [];

        let limit = result.length;

        // limit lookup results to 6 items on UI
        if (limit > 7) {
            limit = 7;
        }

        for (let i = 0; i < limit; i++) {
            const d = this.getDestinationViewModel(result[i]);

            destinations.push({
                value: d.iataCode,
                label: d.name,
                sublabel: d.parentName,
                stamp: d.iataCode
            });
        }

        return destinations;
    }

    searchDestinations(inputValue, callback) {
        this.startedSearchInput();
        new api.DestinationService().lookup(inputValue).then(
            (result: api.Destination[]) => {
                const destinations = this.destinationsConvert(result);
                callback(destinations);
            },
            () => {
                callback([]);
            }
        );
    }
}
